import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { POST_MAX, REPLY_POLICIES, type BookmarkCollection, type Post, type PostAnalytics, type ReplyPolicy } from "@atrium/shared";
import { ApiError, api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { compact, fullDate } from "../../lib/format";
import { updatePostEverywhere } from "../../lib/queryClient";
import { Dialog } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { Field, Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Settings";
import { Switch } from "../../components/ui/Controls";
import { Spinner } from "../../components/ui/Spinner";
import { Tooltip } from "../../components/ui/Tooltip";
import { toast } from "../../components/ui/Toast";
import { IconCheck, IconPlus } from "../../components/icons";
import { IconFolder } from "../../components/socialIcons";
import { MentionTextarea } from "./MentionTextarea";
import { reactionMeta } from "./Reactions";
import { usePostActions } from "./usePostActions";

/*
 * One host for the post dialogs (edit, analytics, save to collection), so a
 * long feed doesn't mount a dialog per card. Cards call openPostDialog().
 */
type Kind = "edit" | "analytics" | "save";
const useDialogs = create<{ kind: Kind | null; post: Post | null; open: (k: Kind, p: Post) => void; close: () => void }>((set) => ({
  kind: null,
  post: null,
  open: (kind, post) => set({ kind, post }),
  close: () => set({ kind: null }),
}));
export const openPostDialog = (kind: Kind, post: Post) => useDialogs.getState().open(kind, post);

export function PostDialogsHost() {
  const { kind, post, close } = useDialogs();
  return (
    <>
      <EditPostDialog post={kind === "edit" ? post : null} onClose={close} />
      <AnalyticsDialog post={kind === "analytics" ? post : null} onClose={close} />
      <SaveDialog post={kind === "save" ? post : null} onClose={close} />
    </>
  );
}

export const REPLY_POLICY_LABEL: Record<ReplyPolicy, string> = {
  everyone: "Everyone can reply",
  following: "People you follow",
  mentioned: "Only people you mention",
  none: "No one",
};

/* ───────────── Edit ───────────── */

function EditPostDialog({ post, onClose }: { post: Post | null; onClose: () => void }) {
  const [text, setText] = useState("");
  const [cw, setCw] = useState("");
  const [sensitive, setSensitive] = useState(false);
  const [policy, setPolicy] = useState<ReplyPolicy>("everyone");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!post) return;
    setText(post.content);
    setCw(post.contentWarning ?? "");
    setSensitive(post.sensitive);
    setPolicy(post.replyPolicy);
    setLocation(post.location ?? "");
    setError(null);
  }, [post]);

  const save = async () => {
    if (!post) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.patch<Post>(`/api/posts/${post.id}`, { content: text, contentWarning: cw.trim() || null, sensitive, replyPolicy: policy, location: location.trim() || null });
      updatePostEverywhere(post.id, (p) => ({ ...p, ...updated }));
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!post}
      onOpenChange={(o) => !o && onClose()}
      title="Edit post"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={text.length > POST_MAX} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <MentionTextarea value={text} onChange={setText} rows={4} maxLength={POST_MAX} aria-label="Post text" className="w-full rounded-md border border-line-strong bg-bg px-3 py-2 text-base leading-6" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Content warning">{(p) => <Input {...p} value={cw} maxLength={120} placeholder="None" onChange={(e) => setCw(e.target.value)} />}</Field>
          <Field label="Location">{(p) => <Input {...p} value={location} maxLength={80} placeholder="None" onChange={(e) => setLocation(e.target.value)} />}</Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-fg-2">
            <Switch label="Sensitive media" checked={sensitive || !!cw.trim()} disabled={!!cw.trim()} onCheckedChange={setSensitive} />
            Sensitive media
          </label>
          <Select<ReplyPolicy> aria-label="Who can reply" value={policy} onChange={setPolicy} options={REPLY_POLICIES.map((v) => ({ value: v, label: REPLY_POLICY_LABEL[v] }))} />
        </div>
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/* ───────────── Analytics ───────────── */

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg bg-surface px-3 py-2.5">
      <div className="text-xs text-fg-3">{label}</div>
      <div className="text-xl font-semibold text-fg tabular" title={hint}>
        {value}
      </div>
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = { feed: "Feed", profile: "Profile", post: "Post page", search: "Search", community: "Community", saved: "Saved" };

/** Views by day as single-series bars (the title names the series; no legend needed). */
function ViewsByDay({ days }: { days: PostAnalytics["viewsByDay"] }) {
  // Fill the last 14 days so gaps show as empty days rather than disappearing.
  const end = new Date();
  const series = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(end.getTime() - (13 - i) * 86400_000).toISOString().slice(0, 10);
    return { day: d, views: days.find((x) => x.day === d)?.views ?? 0 };
  });
  const max = Math.max(1, ...series.map((s) => s.views));
  const label = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return (
    <figure>
      <figcaption className="mb-2 text-sm font-medium text-fg">Views, last 14 days</figcaption>
      <div className="flex h-32 items-end gap-[2px] border-b border-line" role="img" aria-label={`Views per day: ${series.map((s) => `${label(s.day)} ${s.views}`).join(", ")}`}>
        {series.map((s) => (
          <Tooltip key={s.day} label={`${label(s.day)}: ${s.views} ${s.views === 1 ? "view" : "views"}`}>
            <div className="group flex h-full flex-1 items-end" tabIndex={0}>
              <div className="w-full rounded-t-[4px] bg-accent transition-opacity group-hover:opacity-80" style={{ height: `${Math.max(s.views ? 4 : 0, (s.views / max) * 100)}%` }} />
            </div>
          </Tooltip>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-xs text-fg-3 tabular">
        <span>{label(series[0].day)}</span>
        <span>Today</span>
      </div>
    </figure>
  );
}

function AnalyticsDialog({ post, onClose }: { post: Post | null; onClose: () => void }) {
  const q = useQuery({ queryKey: ["post-analytics", post?.id], queryFn: () => api.get<PostAnalytics>(`/api/posts/${post!.id}/analytics`), enabled: !!post });
  const a = q.data;
  const totalSources = a?.viewsBySource.reduce((n, s) => n + s.views, 0) ?? 0;
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()} title="Post activity" description={post ? `Posted ${fullDate(post.createdAt)}` : undefined} size="lg">
      {q.isPending ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner className="size-5 text-fg-3" />
        </div>
      ) : q.isError || !a ? (
        <p className="py-8 text-center text-sm text-fg-3">{errorMessage(q.error, "Couldn't load activity")}</p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Views" value={compact(a.views)} />
            <Stat label="Engagement" value={`${(a.engagementRate * 100).toFixed(a.engagementRate < 0.1 ? 1 : 0)}%`} hint="Reactions, comments, reposts and saves per view" />
            <Stat label="Reactions" value={compact(a.likes)} />
            <Stat label="Comments" value={compact(a.comments)} />
            <Stat label="Reposts" value={compact(a.reposts)} />
            <Stat label="Quotes" value={compact(a.quotes)} />
            <Stat label="Saves" value={compact(a.bookmarks)} />
          </div>
          <ViewsByDay days={a.viewsByDay} />
          <div className="grid gap-5 sm:grid-cols-2">
            <section>
              <h3 className="mb-2 text-sm font-medium text-fg">Where views came from</h3>
              {a.viewsBySource.length === 0 ? (
                <p className="text-sm text-fg-3">No views yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {[...a.viewsBySource]
                    .sort((x, y) => y.views - x.views)
                    .map((s) => (
                      <li key={s.source} className="text-sm">
                        <div className="flex justify-between text-fg-2">
                          <span>{SOURCE_LABEL[s.source] ?? s.source}</span>
                          <span className="tabular">{Math.round((s.views / Math.max(1, totalSources)) * 100)}%</span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2">
                          <div className="h-full rounded-full bg-accent" style={{ width: `${(s.views / Math.max(1, totalSources)) * 100}%` }} />
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </section>
            <section>
              <h3 className="mb-2 text-sm font-medium text-fg">Reactions</h3>
              {a.reactions.length === 0 ? (
                <p className="text-sm text-fg-3">No reactions yet.</p>
              ) : (
                <ul className="space-y-1">
                  {[...a.reactions]
                    .sort((x, y) => y.n - x.n)
                    .map((r) => {
                      const m = reactionMeta(r.kind as Post["myReaction"]);
                      return (
                        <li key={r.kind} className="flex items-center gap-2 text-sm text-fg-2">
                          <m.icon className={cn("icon size-[18px]", m.tone)} aria-hidden />
                          <span className="flex-1">{m.label}</span>
                          <span className="tabular text-fg">{compact(r.n)}</span>
                        </li>
                      );
                    })}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/* ───────────── Save to collection ───────────── */

export function useCollections(enabled = true) {
  return useQuery({ queryKey: ["collections"], queryFn: () => api.get<{ items: BookmarkCollection[] }>("/api/collections").then((r) => r.items), enabled });
}

function SaveDialog({ post, onClose }: { post: Post | null; onClose: () => void }) {
  const qc = useQueryClient();
  const actions = usePostActions();
  const collections = useCollections(!!post);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setName(""), [post]);

  const saveTo = async (c: BookmarkCollection | null) => {
    if (!post) return;
    await actions.bookmark(post, { collectionId: c?.id ?? null, collectionName: c?.name ?? "Saved" });
    onClose();
  };
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const c = await api.post<BookmarkCollection>("/api/collections", { name });
      void qc.invalidateQueries({ queryKey: ["collections"] });
      await saveTo(c);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()} title="Save to collection" size="sm">
      <div className="-mx-2 space-y-px">
        <button type="button" onClick={() => void saveTo(null)} className="flex h-10 w-full items-center gap-3 rounded-md px-2 text-left text-sm hover:bg-surface">
          <IconFolder className="icon text-fg-3" aria-hidden />
          <span className="flex-1">Saved, no collection</span>
        </button>
        {collections.data?.map((c) => (
          <button key={c.id} type="button" onClick={() => void saveTo(c)} className="flex h-10 w-full items-center gap-3 rounded-md px-2 text-left text-sm hover:bg-surface">
            <IconFolder className="icon text-fg-3" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{c.name}</span>
            <span className="text-xs text-fg-3 tabular">{c.count}</span>
          </button>
        ))}
        {collections.isPending && <Spinner className="mx-auto my-3 size-4 text-fg-3" />}
      </div>
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <Input value={name} maxLength={50} onChange={(e) => setName(e.target.value)} placeholder="New collection" aria-label="New collection name" className="!h-9" />
        <Button type="submit" icon={name.trim() ? IconCheck : IconPlus} loading={busy} disabled={!name.trim()}>
          Create
        </Button>
      </form>
    </Dialog>
  );
}
